import React from 'react';
import {
  EnrichmentAlert,
  EnrichmentDataTable,
  EnrichmentField,
  EnrichmentFieldGrid,
  EnrichmentSignalList,
  formatFieldLabel,
  formatValue,
} from '../primitives';

const HIDDEN_KEYS = new Set(['source', 'cached']);

function flattenForGrid(data, prefix = '') {
  const rows = [];
  Object.entries(data || {}).forEach(([key, value]) => {
    if (HIDDEN_KEYS.has(key)) return;
    const label = prefix ? `${prefix} · ${formatFieldLabel(key)}` : formatFieldLabel(key);
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(...flattenForGrid(value, label));
      return;
    }
    if (Array.isArray(value) && value.length && typeof value[0] === 'object') return;
    rows.push({ label, value });
  });
  return rows;
}

function arrayToTable(items) {
  if (!items?.length || typeof items[0] !== 'object') return null;
  const keys = [...new Set(items.flatMap((row) => Object.keys(row)))].slice(0, 6);
  return {
    columns: keys.map((key) => ({ key, label: formatFieldLabel(key) })),
    rows: items,
  };
}

export default function GenericSectionBody({ section, data }) {
  const { status } = section;

  if (status === 'skipped') {
    return (
      <EnrichmentAlert variant="muted">
        {data?.reason || 'This enrichment step was skipped.'}
      </EnrichmentAlert>
    );
  }

  if (status === 'error') {
    return (
      <EnrichmentAlert variant="error">
        {data?.error || 'Enrichment failed for this section.'}
      </EnrichmentAlert>
    );
  }

  if (!data || typeof data !== 'object') {
    return <EnrichmentAlert variant="muted">No structured data available.</EnrichmentAlert>;
  }

  const signals = data.risk_signals || data.indicators || data.signals;
  const tableFromArray = arrayToTable(
    data.nxx_carriers || data.variants || (Array.isArray(data) ? data : null),
  );
  const fields = flattenForGrid(data).filter(({ value }) => formatValue(value) !== '—' || typeof value === 'boolean');

  return (
    <>
      {data.summary && (
        <EnrichmentAlert variant="info">{data.summary}</EnrichmentAlert>
      )}
      {signals?.length > 0 && <EnrichmentSignalList items={signals} />}
      {fields.length > 0 && (
        <EnrichmentFieldGrid columns={3}>
          {fields.slice(0, 18).map(({ label, value }) => (
            <EnrichmentField
              key={label}
              label={label}
              value={value}
              mono={typeof value === 'string' && (value.includes('+') || value.includes('.'))}
            />
          ))}
        </EnrichmentFieldGrid>
      )}
      {tableFromArray && (
        <EnrichmentDataTable
          columns={tableFromArray.columns}
          rows={tableFromArray.rows}
        />
      )}
      {data.source && (
        <p className="enrichment-source-line">Source: {data.source}{data.cached ? ' (cached)' : ''}</p>
      )}
    </>
  );
}
